
import React from "react";
import {
  MapPin,
  Share2,
  ChevronRight,
  Building2,
  Car,
  Trees,
  Landmark,
  GraduationCap,
  HeartPulse,
  Briefcase,
  ShoppingBag,
  Sparkles,
  CalendarCheck,
  Home,
  BadgeCheck,
  Wind,
  Bath,
  Dumbbell,
  Zap,
  ShieldCheck,
  Ruler,
  Navigation,
  Compass,
  Layers,
} from "lucide-react";

import Footer from "../../HomePage/Footer";
import Navbar from "../../HomePage/Navbar";

/* ============================================================
   PROJECT IMAGES
============================================================ */

/* ============================================================
   PROJECT IMAGES — ONLINE
============================================================ */

const hero =
  "https://granada.brigade-projects.in/gate.png";

const architecture =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=85";

const lifestyle =
  "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1600&q=85";

const locationMap =
  "https://images.unsplash.com/photo-1524666041070-9f8763d5f2f7?auto=format&fit=crop&w=1600&q=85";

/* ============================================================
   PROJECT CONSTANTS
============================================================ */

const WHATSAPP_NUMBER = "919739580142";

const MAPS_URL =
  "https://maps.app.goo.gl/hc4TMzhPXom1rvBMA";

const PROJECT_NAME =
  "Ultra-Premium French-Inspired Rowhouses";

const WHATSAPP_MESSAGE = `Hi, I'm interested in the Ultra-Premium French-Inspired Rowhouses near Soukya Road, Whitefield.

📍 Location: ${MAPS_URL}

🏡 20 Acres
🏠 242 Rowhouses
🏗 G+2 / G+3
📅 Expected Possession: 3 Years

Configurations:
• 4 Bed Optima – ~3,700 sq.ft. – ₹5.40 Cr onwards
• 4 Bed Premia – ~4,000 sq.ft. – ₹5.85 Cr onwards
• 4 Bed Luxe – ~4,500 sq.ft. – ₹6.55 Cr onwards
• 5 Bed Luxe – ~5,500 sq.ft. – ₹8.00 Cr onwards

💰 CSF Amount: ₹25,00,000

Please share the complete project details, floor plans and EOI process.`;

function buildWhatsAppLink() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;
}

/* ============================================================
   REUSABLE COMPONENTS
============================================================ */

function Frame({
  src,
  alt,
  ratio = "aspect-[4/5]",
  className = "",
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-gray-100 ${ratio} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        onError={(e) => {
          e.currentTarget.style.display = "none";

          if (e.currentTarget.nextSibling) {
            e.currentTarget.nextSibling.style.display = "flex";
          }
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div
        style={{ display: "none" }}
        className="absolute inset-0 items-center justify-center px-4 text-center text-xs font-medium uppercase tracking-wide text-gray-400"
      >
        {alt}
      </div>
    </div>
  );
}

function FloatingBadge({
  className = "",
  children,
}) {
  return (
    <div
      className={`absolute rounded-2xl bg-white px-5 py-3 shadow-xl shadow-gray-300/40 ${className}`}
    >
      {children}
    </div>
  );
}

function ChecklistItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">
        <Icon
          className="h-5 w-5 text-red-600"
          strokeWidth={2}
        />
      </div>

      <span className="text-sm font-semibold text-gray-800 sm:text-base">
        {children}
      </span>
    </div>
  );
}

function JumpPill({
  icon: Icon,
  label,
  target,
}) {
  return (
    <a
      href={target}
      className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:border-red-200 hover:text-red-600"
    >
      <Icon
        className="h-4 w-4"
        strokeWidth={2}
      />

      {label}

      <ChevronRight className="h-3.5 w-3.5" />
    </a>
  );
}

function StatPill({
  value,
  label,
}) {
  return (
    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center">
      <p className="text-2xl font-extrabold text-red-600">
        {value}
      </p>

      <p className="mt-0.5 text-xs font-medium text-gray-500">
        {label}
      </p>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
        <Icon
          className="h-5 w-5 text-red-600"
          strokeWidth={1.8}
        />
      </div>

      <h4 className="mt-4 text-base font-extrabold text-gray-900">
        {title}
      </h4>

      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {description}
      </p>
    </div>
  );
}

function ConnectivityGroup({
  icon: Icon,
  title,
  rows,
}) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50">
          <Icon
            className="h-5 w-5 text-red-600"
            strokeWidth={2}
          />
        </div>

        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900">
          {title}
        </h4>
      </div>

      <ul className="mt-4 space-y-3">
        {rows.map((row) => (
          <li
            key={row.name}
            className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-2.5"
          >
            <span className="text-sm font-medium text-gray-700">
              {row.name}
            </span>

            <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-bold text-red-500">
              {row.distance}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConfigurationCard({
  type,
  area,
  price,
}) {
  return (
    <div className="group rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="flex items-center justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
          <Home
            className="h-5 w-5 text-red-600"
            strokeWidth={1.8}
          />
        </div>

        <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-600">
          Available
        </span>
      </div>

      <p className="mt-5 text-sm font-extrabold uppercase tracking-wide text-red-600">
        {type}
      </p>

      <p className="mt-2 text-2xl font-extrabold text-gray-900">
        {price}
      </p>

      <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
        <Ruler className="h-4 w-4 text-red-500" />

        {area}
      </div>

      <p className="mt-2 text-xs text-gray-400">
        Approx. area · Excluding GST
      </p>
    </div>
  );
}

function USPItem({
  icon: Icon,
  children,
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">
        <Icon
          className="h-4 w-4 text-red-600"
          strokeWidth={2}
        />
      </div>

      <span className="text-sm leading-relaxed text-gray-700">
        {children}
      </span>
    </div>
  );
}

/* ============================================================
   DATA
============================================================ */

const PROJECT_STATS = [
  {
    value: "20",
    label: "Acres",
  },
  {
    value: "242",
    label: "Rowhouses",
  },
  {
    value: "G+2/G+3",
    label: "Structure",
  },
  {
    value: "3 Years",
    label: "Expected Possession",
  },
];

const CONFIGURATIONS = [
  {
    type: "4 Bed Optima",
    area: "Approx. 3,700 sq.ft.",
    price: "₹5.40 Cr onwards",
  },
  {
    type: "4 Bed Premia",
    area: "Approx. 4,000 sq.ft.",
    price: "₹5.85 Cr onwards",
  },
  {
    type: "4 Bed Luxe",
    area: "Approx. 4,500 sq.ft.",
    price: "₹6.55 Cr onwards",
  },
  {
    type: "5 Bed Luxe",
    area: "Approx. 5,500 sq.ft.",
    price: "₹8.00 Cr onwards",
  },
];

const LOCATION_POINTS = [
  {
    name: "Hope Farm Junction",
    distance: "6.8 km",
  },
  {
    name: "NH 648",
    distance: "900 m",
  },
  {
    name: "ITPL",
    distance: "~25 mins",
  },
  {
    name: "Ayurvedagram Resort",
    distance: "Adjacent",
  },
];

const CONNECTIVITY = [
  {
    icon: GraduationCap,
    title: "Education",
    rows: [
      {
        name: "Leading schools",
        distance: "Nearby",
      },
      {
        name: "Colleges",
        distance: "Nearby",
      },
      {
        name: "International schools",
        distance: "Easy access",
      },
      {
        name: "Educational institutions",
        distance: "Well connected",
      },
    ],
  },

  {
    icon: HeartPulse,
    title: "Healthcare",
    rows: [
      {
        name: "Leading hospitals",
        distance: "Nearby",
      },
      {
        name: "Speciality healthcare",
        distance: "Easy access",
      },
      {
        name: "Clinics",
        distance: "Nearby",
      },
    ],
  },

  {
    icon: Briefcase,
    title: "Work & Tech",
    rows: [
      {
        name: "ITPL",
        distance: "~25 mins",
      },
      {
        name: "Whitefield Tech Parks",
        distance: "Easy access",
      },
      {
        name: "East Bengaluru",
        distance: "Strategic",
      },
    ],
  },

  {
    icon: ShoppingBag,
    title: "Lifestyle",
    rows: [
      {
        name: "Premium malls",
        distance: "Nearby",
      },
      {
        name: "Restaurants",
        distance: "Nearby",
      },
      {
        name: "Retail & entertainment",
        distance: "Easy access",
      },
    ],
  },
];

const ARCHITECTURE_FEATURES = [
  {
    icon: Building2,
    title: "French-Inspired Architecture",
    description:
      "Elegant French-inspired architectural language designed to create a distinctive premium residential environment.",
  },

  {
    icon: Compass,
    title: "Vastu-Compliant Homes",
    description:
      "Thoughtfully planned residences designed with Vastu principles in mind.",
  },

  {
    icon: Trees,
    title: "Central Courtyard",
    description:
      "A central courtyard brings abundant natural light, ventilation and openness into the community.",
  },

  {
    icon: Building2,
    title: "Private Lift",
    description:
      "Every unit is planned with a branded private lift for enhanced convenience and privacy.",
  },

  {
    icon: Wind,
    title: "Premium Glazing",
    description:
      "Premium aluminium-glazing windows designed to complement the architecture and lifestyle.",
  },

  {
    icon: Home,
    title: "Premium Flooring",
    description:
      "Marble flooring in common areas with wooden laminate flooring in the master bedroom.",
  },
];

const LUXURY_FEATURES = [
  {
    icon: Bath,
    title: "Master Bathrooms",
    description:
      "Bathtubs provided in two master bedrooms for a refined private-bath experience.",
  },

  {
    icon: Car,
    title: "2 Independent Car Parks",
    description:
      "Each residence is planned with two independent car parking spaces.",
  },

  {
    icon: Zap,
    title: "Dedicated EV Charging",
    description:
      "One dedicated EV charging point for convenient electric mobility.",
  },

  {
    icon: ShieldCheck,
    title: "Minimal Common Walls",
    description:
      "Minimal or negligible common walls help provide enhanced privacy between residences.",
  },

  {
    icon: Trees,
    title: "Temperature-Controlled Pool",
    description:
      "A temperature-controlled swimming pool designed as part of the premium lifestyle amenities.",
  },

  {
    icon: Dumbbell,
    title: "Lifestyle Amenities",
    description:
      "Thoughtfully designed amenities created around comfort, recreation and premium community living.",
  },
];

/* ============================================================
   PAGE
============================================================ */

export default function PremiumFrenchRowhouses() {
  return (
    <div className="bg-white text-gray-900">

      <Navbar />

      {/* ======================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden bg-gradient-to-b from-red-50/80 via-red-50/20 to-white px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          {/* LEFT */}

          <div>

            <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-sm font-bold text-red-600">
              <Sparkles className="h-4 w-4" />

              Launching Soon · EOI Open
            </div>

            <p className="mt-6 text-sm font-bold uppercase tracking-wider text-red-600">
              Ultra-Premium Rowhouses · East Bengaluru
            </p>

            <h1 className="mt-3 text-4xl font-extrabold leading-[1.08] text-gray-900 sm:text-5xl lg:text-6xl">
              French-Inspired
              <span className="text-red-600">
                {" "}Luxury
              </span>
              <br />
              Near Whitefield
            </h1>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg">
              An exclusive ultra-premium rowhouse community near
              Soukya Road, Whitefield, combining elegant French-inspired
              architecture, privacy, connectivity and thoughtfully
              designed luxury living.
            </p>

            {/* HERO ACTIONS */}

            <div className="mt-8 flex flex-wrap gap-3">

              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700"
              >
                Contact & Enquire
              </a>

              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
              >
                <Share2 className="h-4 w-4" />

                Enquire on WhatsApp
              </a>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-6 py-3.5 text-sm font-bold text-red-600 transition hover:bg-red-50"
              >
                <MapPin className="h-4 w-4" />

                View Location
              </a>

            </div>

            {/* HERO CHECKLIST */}

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">

              <ChecklistItem icon={Building2}>
                20-Acre Community
              </ChecklistItem>

              <ChecklistItem icon={Home}>
                242 Rowhouses
              </ChecklistItem>

              <ChecklistItem icon={Layers}>
                G+2 / G+3 Structure
              </ChecklistItem>

              <ChecklistItem icon={CalendarCheck}>
                Possession in 3 Years
              </ChecklistItem>

            </div>

          </div>

          {/* RIGHT IMAGE */}

          <div className="relative">

            <Frame
              src={hero}
              alt="Ultra-premium French-inspired rowhouses near Whitefield"
              ratio="aspect-[4/3]"
            />

            <FloatingBadge className="-left-3 top-6 sm:-left-6">

              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Starting From
              </p>

              <p className="mt-1 text-xl font-extrabold text-red-600">
                ₹5.40 Cr*
              </p>

              <p className="text-xs text-gray-500">
                4 Bed Optima
              </p>

            </FloatingBadge>

            <FloatingBadge className="-bottom-5 -right-3 sm:-right-6">

              <p className="text-xl font-extrabold text-gray-900">
                242
              </p>

              <p className="text-xs text-gray-500">
                Premium Rowhouses
              </p>

            </FloatingBadge>

          </div>

        </div>

      </section>

      {/* ======================================================
          QUICK STATS
      ====================================================== */}

      <section className="px-5 sm:px-10">

        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-4">

          {PROJECT_STATS.map((stat) => (
            <StatPill
              key={stat.label}
              value={stat.value}
              label={stat.label}
            />
          ))}

        </div>

      </section>

      {/* ======================================================
          PROJECT OVERVIEW
      ====================================================== */}

      <section
        id="overview"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">

          <Frame
            src={architecture}
            alt="French-inspired rowhouse architecture"
            ratio="aspect-[4/3]"
          />

          <div>

            <p className="text-sm font-bold uppercase tracking-wide text-red-600">
              Project Overview
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              A rare combination of privacy, architecture and luxury.
            </h2>

            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">
              This exclusive rowhouse community is designed for
              discerning homeowners looking for a refined lifestyle in
              East Bengaluru. The development combines French-inspired
              architecture with spacious residences, thoughtful
              planning and excellent connectivity.
            </p>

            <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
              Every residence is designed around comfort and privacy,
              with central courtyards, private lifts, premium finishes,
              dedicated parking and modern lifestyle amenities.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Land Parcel
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  20 Acres
                </p>

              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">

                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Total Homes
                </p>

                <p className="mt-1 text-2xl font-extrabold text-gray-900">
                  242
                </p>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          CONFIGURATIONS & PRICING
      ====================================================== */}

      <section
        id="configurations"
        className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Row House Typologies
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose your expression of luxury.
          </h2>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-gray-600">
            Premium 4-bedroom and 5-bedroom rowhouse configurations
            designed for spacious family living.
          </p>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

            {CONFIGURATIONS.map((configuration) => (
              <ConfigurationCard
                key={configuration.type}
                {...configuration}
              />
            ))}

          </div>

          <div className="mt-6 rounded-2xl border border-yellow-200 bg-yellow-50 px-5 py-4 text-sm text-yellow-800">
            <strong>Pricing note:</strong> Prices mentioned above are
            starting prices and exclude GST. Please confirm the latest
            price list, availability and applicable charges with the
            sales team.
          </div>

        </div>

      </section>

      {/* ======================================================
          LOCATION
      ====================================================== */}

      <section
        id="location"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-red-600">
                Strategic Location
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Connected to the best of East Bengaluru.
              </h2>

              <p className="mt-4 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base">
                Located near Soukya Road in Whitefield, the project
                enjoys access to major roads, technology hubs, schools,
                healthcare, shopping and lifestyle destinations.
              </p>

              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-red-200 transition hover:bg-red-700"
              >
                <Navigation className="h-4 w-4" />

                Open in Google Maps
              </a>

            </div>

            <Frame
              src={locationMap}
              alt="Project location near Soukya Road Whitefield Bengaluru"
              ratio="aspect-[4/3]"
            />

          </div>

          {/* LOCATION POINTS */}

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">

            {LOCATION_POINTS.map((point) => (
              <div
                key={point.name}
                className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm"
              >

                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
                  <MapPin className="h-5 w-5 text-red-600" />
                </div>

                <p className="mt-4 text-base font-extrabold text-gray-900">
                  {point.name}
                </p>

                <p className="mt-1 text-sm font-bold text-red-600">
                  {point.distance}
                </p>

              </div>
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          CONNECTIVITY
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <p className="text-sm font-bold uppercase tracking-wide text-red-600">
            Everything Within Reach
          </p>

          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Everyday convenience, naturally connected.
          </h2>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">

            {CONNECTIVITY.map((group) => (
              <ConnectivityGroup
                key={group.title}
                {...group}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          ARCHITECTURE & DESIGN
      ====================================================== */}

      <section
        id="architecture"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-red-600">
                Crafted for Luxury Living
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Details that define the experience.
              </h2>

              <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                From architecture to finishes, every element has been
                considered to create a sophisticated and comfortable
                rowhouse lifestyle.
              </p>

              <div className="mt-8 space-y-4">

                <USPItem icon={Building2}>
                  Elegant French-inspired architecture
                </USPItem>

                <USPItem icon={Compass}>
                  Vastu-compliant units
                </USPItem>

                <USPItem icon={Trees}>
                  Central courtyard for natural light and ventilation
                </USPItem>

                <USPItem icon={Building2}>
                  Branded private lift in every unit
                </USPItem>

                <USPItem icon={Wind}>
                  Premium aluminium-glazing windows
                </USPItem>

                <USPItem icon={Home}>
                  Marble flooring in common areas
                </USPItem>

              </div>

            </div>

            <Frame
              src={lifestyle}
              alt="Premium French-inspired rowhouse lifestyle"
              ratio="aspect-[4/3]"
            />

          </div>

        </div>

      </section>

      {/* ======================================================
          ARCHITECTURE FEATURE CARDS
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {ARCHITECTURE_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                {...feature}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          PREMIUM FEATURES
      ====================================================== */}

      <section className="px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="text-center">

            <p className="text-sm font-bold uppercase tracking-wide text-red-600">
              Designed for Convenience
            </p>

            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              A lifestyle built around you.
            </h2>

          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {LUXURY_FEATURES.map((feature) => (
              <FeatureCard
                key={feature.title}
                {...feature}
              />
            ))}

          </div>

        </div>

      </section>

      {/* ======================================================
          WORLD CLASS AMENITIES
      ====================================================== */}

      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">

            <Frame
              src={hero}
              alt="Premium lifestyle amenities"
              ratio="aspect-[4/3]"
            />

            <div>

              <p className="text-sm font-bold uppercase tracking-wide text-red-600">
                World-Class Amenities
              </p>

              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                More than a home. A complete lifestyle.
              </h2>

              <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                The community is planned with thoughtfully designed
                amenities that complement the premium character of the
                residences.
              </p>

              <div className="mt-8 space-y-4">

                <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">

                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-50">
                    <Trees className="h-5 w-5 text-red-600" />
                  </div>

                  <div>
                    <p className="font-bold text-gray-900">
                      Temperature-Controlled Swimming Pool
                    </p>

                    <p className="mt-1 text-sm text-gray-500">
                      Designed for year-round recreation and relaxation.
                    </p>
                  </div>

                </div>

                <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">

                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-50">
                    <Dumbbell className="h-5 w-5 text-red-600" />
                  </div>

                  <div>
                    <p className="font-bold text-gray-900">
                      Premium Lifestyle Amenities
                    </p>

                    <p className="mt-1 text-sm text-gray-500">
                      Thoughtfully planned spaces for leisure and community living.
                    </p>
                  </div>

                </div>

                <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">

                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-50">
                    <Trees className="h-5 w-5 text-red-600" />
                  </div>

                  <div>
                    <p className="font-bold text-gray-900">
                      Landscaped Community Spaces
                    </p>

                    <p className="mt-1 text-sm text-gray-500">
                      Green spaces and courtyards designed around natural light and ventilation.
                    </p>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          EOI DETAILS
      ====================================================== */}

      <section
        id="eoi"
        className="px-5 py-16 sm:px-10 sm:py-20"
      >

        <div className="mx-auto max-w-5xl">

          <div className="rounded-[32px] border border-red-100 bg-red-50 p-7 sm:p-10">

            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">

              <div>

                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-red-600 shadow-sm">

                  <BadgeCheck className="h-4 w-4" />

                  EOI Open
                </div>

                <h2 className="mt-5 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                  Secure your priority opportunity.
                </h2>

                <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                  Limited EOI slots are available. Connect with the
                  sales team for project details, configuration
                  availability, pricing and the EOI process.
                </p>

              </div>

              <div className="rounded-3xl bg-white p-6 shadow-sm">

                <div className="flex items-center justify-between border-b border-gray-100 pb-5">

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                      CSF Amount
                    </p>

                    <p className="mt-1 text-3xl font-extrabold text-red-600">
                      ₹25,00,000
                    </p>
                  </div>

                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">
                    <CalendarCheck className="h-5 w-5 text-red-600" />
                  </div>

                </div>

                <div className="mt-5">

                  <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                    KYC Documents Required
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2">

                    <span className="rounded-full bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                      PAN Card
                    </span>

                    <span className="rounded-full bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                      Aadhaar Card
                    </span>

                  </div>

                </div>

                <a
                  href="https://wa.me/message/MDG2ZSZNG6P6G1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 px-6 py-4 text-sm font-bold text-white transition hover:bg-red-700"
                >
                  Contact & Enquire for EOI
                </a>

              </div>

            </div>

          </div>

        </div>

      </section>

      {/* ======================================================
          QUICK NAVIGATION
      ====================================================== */}

      <section className="px-5 pb-16 sm:px-10">

        <div className="mx-auto flex max-w-6xl flex-wrap justify-center gap-3">

          <JumpPill
            icon={Home}
            label="Overview"
            target="#overview"
          />

          <JumpPill
            icon={Ruler}
            label="Configurations"
            target="#configurations"
          />

          <JumpPill
            icon={MapPin}
            label="Location"
            target="#location"
          />

          <JumpPill
            icon={Building2}
            label="Architecture"
            target="#architecture"
          />

          <JumpPill
            icon={CalendarCheck}
            label="EOI"
            target="#eoi"
          />

        </div>

      </section>

      {/* ======================================================
          FINAL CTA
      ====================================================== */}

      <section className="bg-gradient-to-r from-red-600 to-red-500 px-5 py-16 text-center sm:px-10 sm:py-20">

        <Sparkles
          className="mx-auto h-7 w-7 text-white"
          strokeWidth={1.6}
        />

        <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold text-white sm:text-4xl">
          A rare combination of location, privacy, architecture & luxury.
        </h2>

        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-red-50">
          Explore configuration-wise pricing, project details,
          location and EOI availability for this upcoming
          ultra-premium rowhouse community near Whitefield.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-red-600 shadow-lg transition hover:bg-red-50"
          >
            Contact & Enquire
          </a>

          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-200 transition hover:bg-green-600"
          >
            <Share2 className="h-4 w-4" />

            WhatsApp Enquiry
          </a>

          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20"
          >
            <MapPin className="h-4 w-4" />

            View Location
          </a>

        </div>

        <p className="mt-5 text-xs font-semibold text-red-100">
          Limited EOI slots · Contact for latest availability and pricing
        </p>

      </section>

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <Footer />

      {/* ======================================================
          FLOATING WHATSAPP
      ====================================================== */}

      <a
        href={buildWhatsAppLink()}
        target="_blank"
        rel="noreferrer"
        aria-label="Enquire about the property on WhatsApp"
        className="fixed bottom-6 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-xl shadow-green-300/50 transition-transform hover:scale-105 sm:bottom-8 sm:right-8"
      >
        <Share2 className="h-5 w-5" />
      </a>

    </div>
  );
}
